import isEmpty from 'validator/lib/isEmpty';
import isLength from 'validator/lib/isLength';

import {
  GENERATION_TOOL,
  type GenerationTool,
  isSummarizerGenerationTool,
} from 'src/data/generationTool';
import { formatNumber } from 'src/lib/format';
import { countChars, countWords } from 'src/lib/string';

import { BRIEF_TYPE, type BriefType } from './const';
import type { BriefContent } from './types';

export const COPIES_MAX_WORD_COUNT = 150;
export const ARTICLE_TITLE_MAX_CHARS_COUNT = 240;
export const ARTICLE_BODY_MAX_CHARS_COUNT = 2400;
export const EMAIL_BRIEF_MAX_CHARS = 3000;
export const EMAIL_PROMOTION_MAX_CHARS = 80;
export const SOCIAL_MANUAL_MAX_CHARS_COUNT = 600;
export const PRODUCT_NAME_MAX_CHARS = 120;
export const PRODUCT_DESCRIPTION_MAX_CHARS = 1200;
export const FREESTYLE_DESCRIPTION_MAX_WORDS = 10_000;

const FREE_TEXT_MAX_CHAR_COUNT = 4800;
const FREE_TEXT_MAX_WORD_COUNT = 1000;

export const EMPTY_FIELD_ERROR = 'Input needed';

const getMaxCharsError = (chars: number): string => `${formatNumber(chars)} characters maximum`;

const avoidLimitMaxWordCount = (url: string): boolean => {
  return url ? url.includes('walmart.com') : false;
};

const validateFieldValue = (
  value: string | undefined,
  { maxChars, useMaxLimit = true }: { maxChars: number; useMaxLimit?: boolean }
): string[] => {
  const messages = [];

  const alignedValue = value || '';
  if (isEmpty(alignedValue, { ignore_whitespace: true })) {
    messages.push(EMPTY_FIELD_ERROR);
  } else if (countChars(alignedValue) > maxChars && useMaxLimit) {
    messages.push(getMaxCharsError(maxChars));
  }

  return messages;
};

const validateBriefContent = (
  content: BriefContent,
  { nameMaxChars, bodyMaxChars }: { nameMaxChars: number; bodyMaxChars: number }
) => {
  const url = content?.url || '';
  const title = content?.title || '';
  const body = content?.article || '';

  const useMaxWordsLimit = !avoidLimitMaxWordCount(url);
  const messagesTitle = validateFieldValue(title, {
    maxChars: nameMaxChars,
    useMaxLimit: useMaxWordsLimit,
  });
  const messagesBody = validateFieldValue(body, {
    maxChars: bodyMaxChars,
    useMaxLimit: useMaxWordsLimit,
  });

  const valid = messagesTitle.length === 0 && messagesBody.length === 0;

  return {
    valid,
    title: messagesTitle,
    article: messagesBody,
  };
};

export const validateProductBrief = (content: BriefContent) =>
  validateBriefContent(content, {
    nameMaxChars: PRODUCT_NAME_MAX_CHARS,
    bodyMaxChars: PRODUCT_DESCRIPTION_MAX_CHARS,
  });
const validateContentBrief = (content: BriefContent) =>
  validateBriefContent(content, {
    nameMaxChars: ARTICLE_TITLE_MAX_CHARS_COUNT,
    bodyMaxChars: ARTICLE_BODY_MAX_CHARS_COUNT,
  });

const validateBriefBody = (content: BriefContent, { maxChars }: { maxChars: number }) => {
  const body = content?.body || '';
  const messages = validateFieldValue(body, {
    maxChars,
  });
  const valid = messages.length === 0;

  return {
    valid,
    body: messages,
  };
};

const validateEmailBrief = (content: BriefContent) =>
  validateBriefBody(content, { maxChars: EMAIL_BRIEF_MAX_CHARS });

const validateEmailPromotionField = (content?: string) => {
  const messages = validateFieldValue(content, {
    maxChars: EMAIL_PROMOTION_MAX_CHARS,
  });

  const valid = messages.length === 0;

  return {
    valid,
    messages,
  };
};

const validateEmailPromotionalBrief = (
  content: BriefContent,
  { skipPromo }: { skipPromo: boolean }
) => {
  const productValidation = validateProductBrief(content);
  const promoValidation = !skipPromo
    ? validateEmailPromotionField(content?.emailPromotion)
    : {
        valid: true,
        messages: [],
      };

  return {
    ...productValidation,
    emailPromotion: promoValidation.messages,
    valid: productValidation.valid && promoValidation.valid,
  };
};

const validateSocialManualBrief = (content: BriefContent) =>
  validateBriefBody(content, { maxChars: SOCIAL_MANUAL_MAX_CHARS_COUNT });

export const BLOG_DESCRIPTION_MIN_WORDS_COUNT = 5;
export const BLOG_DESCRIPTION_MAX_CHAR_COUNT = 1000;
export const BLOG_KEYWORDS_MAX_COUNT = 20;
export const BLOG_MAX_WORDS_PER_KEYWORD = 8;
export const BLOG_TARGET_KEYWORDS_MAX_COUNT = 2;
export const BLOG_TARGET_MAX_WORDS_PER_KEYWORD = 8;

export const validateBlogPostBuilderBrief = (content: BriefContent) => {
  const description = content?.description || '';

  const messagesDescription = [];

  const descriptionWordCount = countWords(description);
  if (descriptionWordCount < BLOG_DESCRIPTION_MIN_WORDS_COUNT) {
    messagesDescription.push(
      `Your description should be at least ${BLOG_DESCRIPTION_MIN_WORDS_COUNT} words long`
    );
  } else if (!isLength(description, { max: BLOG_DESCRIPTION_MAX_CHAR_COUNT })) {
    messagesDescription.push(
      `Your description should be up to ${BLOG_DESCRIPTION_MAX_CHAR_COUNT} characters long`
    );
  }

  const valid = messagesDescription.length === 0;

  return {
    valid,
    messages: {
      description: messagesDescription,
    },
  };
};

interface BlogPostBuilderBriefKeywords {
  keyword?: string[];
  targetKeywords?: string[];
}

export const validateBlogPostBuilderBriefKeywords = (
  content: BlogPostBuilderBriefKeywords,
  isTargetKeywordsFlowEnabled = false
) => {
  const briefKeywords = content?.keyword || [];
  const briefTargetKeywords = content?.targetKeywords || [];
  const keywords = isTargetKeywordsFlowEnabled ? briefTargetKeywords : briefKeywords;

  const messagesKeywords = [];

  const maxKeywordsCount = isTargetKeywordsFlowEnabled
    ? BLOG_TARGET_KEYWORDS_MAX_COUNT
    : BLOG_KEYWORDS_MAX_COUNT;
  const maxWordsPerKeyword = isTargetKeywordsFlowEnabled
    ? BLOG_TARGET_MAX_WORDS_PER_KEYWORD
    : BLOG_MAX_WORDS_PER_KEYWORD;

  if (keywords.length > maxKeywordsCount) {
    messagesKeywords.push(`No more than ${maxKeywordsCount} keywords are accepted`);
  }

  const longKeyword = keywords.find((keyword) => countWords(keyword) > maxWordsPerKeyword);
  if (longKeyword) {
    messagesKeywords.push(`Keywords can be up to ${maxWordsPerKeyword} words.`);
  }

  const valid = messagesKeywords.length === 0;

  return {
    valid,
    messages: {
      keywords: messagesKeywords,
    },
  };
};

const isFieldEmpty = (value: string): boolean => {
  return !value || !value.trim();
};

export const getCanvasBriefMaxCharCount = (generationTool?: GenerationTool): number => {
  switch (generationTool) {
    case GENERATION_TOOL.basicEli5:
    case GENERATION_TOOL.basicTalkingPointsToParagraph:
      return 1200;

    case GENERATION_TOOL.basicSentenceExpander:
      return 160;

    case GENERATION_TOOL.basicVideoIdeas:
    case GENERATION_TOOL.basicBlogIdeas:
    case GENERATION_TOOL.basicPromotionalEmail:
      return 80;

    case GENERATION_TOOL.basicQuoraAnswers:
      return 350;

    case GENERATION_TOOL.basicEngagingQuestions:
      return 100;

    default:
      return FREE_TEXT_MAX_CHAR_COUNT;
  }
};

export const getCanvasBriefMaxWordCount = (generationTool?: GenerationTool): number => {
  switch (generationTool) {
    case GENERATION_TOOL.summarizer:
      return 5000;

    default:
      return FREE_TEXT_MAX_WORD_COUNT;
  }
};
export const validateCanvasBrief = (content: BriefContent, generationTool?: GenerationTool) => {
  const freeText = content?.freeText || '';
  const messagesFreeText = [];

  const maxCharCount = getCanvasBriefMaxCharCount(generationTool);
  const maxWordCount = getCanvasBriefMaxWordCount(generationTool);

  if (isFieldEmpty(freeText)) {
    messagesFreeText.push(EMPTY_FIELD_ERROR);
  } else if (generationTool && isSummarizerGenerationTool(generationTool)) {
    if (countWords(freeText) > maxWordCount) {
      messagesFreeText.push(`No more than ${maxWordCount} words are accepted`);
    }
  } else if (countChars(freeText) > maxCharCount) {
    messagesFreeText.push(`No more than ${maxCharCount} characters are accepted`);
  }

  const valid = messagesFreeText.length === 0;

  return {
    valid,
    freeText: messagesFreeText,
  };
};

interface ValidateDocumentBriefOptions {
  briefType: BriefType;
  skipEmailPromotionField?: boolean;
  generationTool?: GenerationTool;
}

export const validateDocumentBrief = (
  briefContent: BriefContent,
  options: ValidateDocumentBriefOptions
) => {
  switch (options?.briefType) {
    case BRIEF_TYPE.unifiedProduct:
      return validateProductBrief(briefContent);

    case BRIEF_TYPE.unifiedContent:
      return validateContentBrief(briefContent);

    case BRIEF_TYPE.email:
      return validateEmailBrief(briefContent);

    case BRIEF_TYPE.unifiedPromotionalEmail:
      return validateEmailPromotionalBrief(briefContent, {
        skipPromo: Boolean(options?.skipEmailPromotionField),
      });

    case BRIEF_TYPE.socialManual:
      return validateSocialManualBrief(briefContent);

    case BRIEF_TYPE.canvas:
      return validateCanvasBrief(briefContent, options?.generationTool);

    default:
      throw new Error(`Unknown brief type to validate: ${options?.briefType}`);
  }
};

interface ValidateFreestyleBriefParams {
  allInstructions: string;
}
export const validateFreestyleBrief = ({ allInstructions }: ValidateFreestyleBriefParams) => {
  const errors = {
    allInstructions: '',
  };

  if (isFieldEmpty(allInstructions)) {
    errors.allInstructions = EMPTY_FIELD_ERROR;
  } else if (countWords(allInstructions) > FREESTYLE_DESCRIPTION_MAX_WORDS) {
    errors.allInstructions = `No more than ${FREESTYLE_DESCRIPTION_MAX_WORDS} words are accepted`;
  }

  return errors;
};
